import Minze, { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import { v4 as uuid } from 'uuid'
import {
  FlowCore,
  IFlowContainer,
  IFlowCore,
  IFlowCoreConfig,
  IFlowTransition,
} from '../../../modules/epflow'
import { Locale } from '../../../modules/locale'
import MainModule from '../../../modules/main'
import { recursiveCleanObject } from '../../../modules/utils'
import { transformFlowDataToContributorFormat, ContributorFlowData } from './utils'

Locale.getInstance().registerKeySet('fr', 'lvcCreateEventContainer', {
  eventCreationFailed: "Impossible de créer l'événement. Veuillez réessayer plus tard.",
})

Locale.getInstance().registerKeySet('en', 'lvcCreateEventContainer', {
  eventCreationFailed: 'Failed to create event. Please try again later.',
})

Locale.getInstance().registerKeySet('fr', 'lvcCreateContributorContainer', {
  contributorCreationFailed: 'La création du contributeur a échoué. Veuillez réessayer.',
})

Locale.getInstance().registerKeySet('en', 'lvcCreateContributorContainer', {
  contributorCreationFailed: 'Contributor creation failed. Please try again.',
})

export interface LvcCreateContributorContainer {}

const STEPS = {
  CREATE_CONTRIBUTOR_INFO: 'createContributorInfo',
  CREATE_CONTRIBUTOR_ADD_IMAGE: 'createContributorAddImage',
  CREATE_CONTRIBUTOR_RECAP: 'createContributorRecap',
  CREATE_CONTRIBUTOR_SUBMITTED: 'createContributorSubmitted',
}

const CORE_CONFIG: IFlowCoreConfig = {
  initialStep: STEPS.CREATE_CONTRIBUTOR_INFO,
  eventType: 'event',
  calcProgress: (data: any, currentStep: IFlowTransition, transitions: IFlowTransition[]) => {
    let indexOf = transitions.indexOf(currentStep)

    return Math.round(100 * (indexOf / (transitions.length - 1)))
  },
  transitions: [
    {
      stepName: STEPS.CREATE_CONTRIBUTOR_INFO,
      elementName: (contextData: any) => `

      <lvc-create-contributor-info flow-step="${STEPS.CREATE_CONTRIBUTOR_INFO}" flow-group="${contextData.flowGroup}">
      </lvc-create-contributor-info>
  `,
      transitionNext: async (data: any) => {
        return STEPS.CREATE_CONTRIBUTOR_ADD_IMAGE
      },
      transitionPrev: async (data: any) => {
        return STEPS.CREATE_CONTRIBUTOR_INFO
      },
    },
    {
      stepName: STEPS.CREATE_CONTRIBUTOR_ADD_IMAGE,
      elementName: (contextData: any) => `
      <lvc-create-contributor-add-image flow-step="${STEPS.CREATE_CONTRIBUTOR_ADD_IMAGE}" flow-group="${contextData.flowGroup}">
      </lvc-create-contributor-add-image>
      `,
      transitionNext: async (data: any) => {
        return STEPS.CREATE_CONTRIBUTOR_RECAP
      },
      transitionPrev: async (data: any) => {
        return STEPS.CREATE_CONTRIBUTOR_INFO
      },
    },
    {
      stepName: STEPS.CREATE_CONTRIBUTOR_RECAP,
      elementName: (contextData: any) => `
      <lvc-create-contributor-recap flow-step="${STEPS.CREATE_CONTRIBUTOR_RECAP}" flow-group="${contextData.flowGroup}">
      </lvc-create-contributor-recap>
      `,
      transitionNext: async (data: any) => {
        return STEPS.CREATE_CONTRIBUTOR_SUBMITTED
      },
      transitionPrev: async (data: any) => {
        return STEPS.CREATE_CONTRIBUTOR_ADD_IMAGE
      },
    },
    {
      stepName: STEPS.CREATE_CONTRIBUTOR_SUBMITTED,
      elementName: (contextData: any) => `
      <lvc-create-contributor-submitted flow-step="${STEPS.CREATE_CONTRIBUTOR_SUBMITTED}" flow-group="${contextData.flowGroup}">
      </lvc-create-contributor-submitted>
      `,
      transitionNext: async (data: any) => {
        return STEPS.CREATE_CONTRIBUTOR_SUBMITTED
      },
      transitionPrev: async (data: any) => {
        return STEPS.CREATE_CONTRIBUTOR_SUBMITTED
      },
    },
  ] as IFlowTransition[],

  onSave: async (data: unknown, instance: IFlowCore): Promise<void> => {
    // Draft system disabled for create contributor flow
  },

  onCommit: async (data: unknown, instance: IFlowCore): Promise<void> => {
    const mainModule = MainModule.getInstance()
    const flowCore = MainModule.getInstance().getFlowManager().getCore('contributorCreation')

    // Transform flow data to contributor format
    const contributorData = transformFlowDataToContributorFormat(data as ContributorFlowData)

    // Call the backend to create the contributor
    const response = await mainModule
      .getAuth()
      .createContributor(flowCore?.entryId || uuid(), contributorData)

    if (!response.success) {
      // If unsuccessful, show error
      console.error('Error during contributor creation:', response.message)
      mainModule.showToastAlert(
        Locale.getKey('lvcCreateContributorContainer.contributorCreationFailed'),
        5000,
        true
      )
    } else {
      // If successful, proceed to next step
      flowCore?.next()
    }
  },
}

export class LvcCreateContributorContainer
  extends MinzeElement
  implements MinzeElement, IFlowContainer
{
  _authHandler: any = null
  flowGroup: string = 'contributorCreation'
  attrs: Attrs = []
  reactive: Reactive = ['stepName']
  flowCore: IFlowCore | null = null
  renderElement: string = ''
  draftId: string | null = null

  async onReady() {
    const mainModule = MainModule.getInstance()
    this._authHandler = this.handleAuthChanged.bind(this)
    mainModule.getEE().on('lvc:user_state_changed', this._authHandler)
    await this.handleAuthChanged({
      isLoggedIn: await mainModule.getAuth().isLoggedIn(),
    })
  }

  handleAuthChanged = async (data: any): Promise<void> => {
    if (!data.isLoggedIn) {
      console.warn('User is not logged in. Aborting draft fetch.')
      return
    }
    const mainModule = MainModule.getInstance()

    this.flowCore = new FlowCore({
      ...CORE_CONFIG,
      flowContainer: this,
    } as IFlowCoreConfig)
    mainModule.getFlowManager().putCore(this.flowCore)

    // Get the draftId from URL parameters.
    const urlParams = new URLSearchParams(window.location.search)
    this.draftId = urlParams.get('draftId')

    if (this.draftId) {
      try {
        const draftData = await mainModule.getAuth().fetchDraftEventById(this.draftId)
        if (draftData) {
          this.flowCore.entryId = this.draftId
          // Load the draft data before initialization.
          this.flowCore.loadDraft(draftData)
        }
      } catch (error) {
        console.error(`Error fetching draft for draftId ${this.draftId}:`, error)
      }
    } else {
      this.flowCore.entryId = uuid()
    }

    // draft data is loaded, initialize FlowCore.
    await this.flowCore.initialize()

    // If a draft was loaded, skip the first step because the first step is the search step.
    if (this.draftId) {
      this.flowCore.next()
    }

    // Notify footer navigation that the flow is ready.
    Minze.cast('lvc:flowcore-ready', this.flowCore)
  }

  async onContainerUpdate(data: any, step: IFlowTransition): Promise<void> {
    window.scrollTo(0, 0)
    let el: string = step?.elementName?.({ flowGroup: this.flowGroup }) || '<span>no element</span>'
    this.renderElement = el
    this.stepName = step.stepName
    this.rerender()
  }

  html = () => {
    let progress: number = this.flowCore?.getProgress() ?? 0
    let saveState = this.flowCore?.getSaveState() ?? 'not_saved'
    return `
		<div class="lvc-create-event-container">
		  <div class="lvc-content">
  			${this.renderElement}
			</div>
			<div class="lvc-footer-container">
				<lvc-footer-navigation
				  progress="${progress}"
				  flow-group="${this.flowGroup}"
				  step="${this.stepName}"
				  data-type="contributor"
				></lvc-footer-navigation>
			</div>
		</div>
		`
  }

  css = () => {
    return `
		.lvc-create-event-container {
			display: flex;
			flex-direction: column;
			min-height: 100vh;
			min-width: 100%;
			align-items: center;
			position: relative;
		}
		.lvc-content {
  		display: flex;
  		max-height: 100%;
  		flex-direction: column;
  		max-width: 600px;
      position: relative;
      padding-bottom: 200px;
      width: 100%;
		}
		.lvc-footer-container {
			display: flex;
			justify-content: center;
			flex-direction: column;
			margin-top: auto;
			width: 100%;
			position: fixed;
			bottom: 0;
		}`
  }

  eventListeners: EventListeners = []
}
